// AuthPages — iOS LIGHT theme (matches base44 design)
// White background, black buttons, Apple blue accents, Google OAuth button.

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AUTH_API, setAuth, getUser } from '../../lib/auth';

const SHELL = (children: ReactNode) => (
  <div className="min-h-screen bg-[#F5F5F7] text-[#1C1C1E] antialiased">
    <header className="sticky top-0 z-20 border-b border-[#C6C6C8]/60 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-14 w-full max-w-[1280px] items-center justify-between px-5 md:h-16 md:px-6">
        <Link to="/" className="flex items-center gap-1 text-[15px] font-normal text-[#007AFF] active:opacity-40" data-testid="auth-back">
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none"><path d="M10.5 1.5L2 10l8.5 8.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Back
        </Link>
        <div className="text-[15px] font-semibold tracking-tight">Funder Pro</div>
        <button onClick={() => window.location.href = '/'} className="text-[15px] font-normal text-[#007AFF] active:opacity-40" data-testid="auth-cancel">Cancel</button>
      </div>
    </header>
    <main className="flex flex-1 flex-col items-center justify-start px-4 pt-8 pb-12 md:pt-12 md:pb-20">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center" data-testid="auth-logo">
          <img src="/cloned/wp-content/uploads/funderpro-logo-dark.svg" alt="Funder Pro" className="h-10 w-auto" />
        </div>
        {children}
      </div>
    </main>
  </div>
);

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  );
}

function GoogleButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-5 py-3 text-base font-medium text-[#1C1C1E] transition hover:bg-gray-50"
    >
      <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Continue with Google
    </button>
  );
}

function Field({ label, name, type = 'text', value, onChange, required, placeholder }: { label: string; name: string; type?: string; value: string; onChange: (v: string) => void; required?: boolean; placeholder?: string }) {
  const [show, setShow] = useState(type !== 'password');
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={type === 'password' && show ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-[#1C1C1E] placeholder:text-gray-400 outline-none transition focus:border-[#007AFF]"
        />
        {type === 'password' && (
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {show ? <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></> : <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>}
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = params.get('next') || '/portal/dashboard';
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await AUTH_API.login(email, password);
      setAuth(res);
      nav(next, { replace: true });
    } catch (e: any) {
      setErr(e?.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  function loginWithGoogle() {
    // In dev mode, the same form works (any email/password works).
    setEmail('demo@funderpro.live');
    setPassword('demo1234');
  }

  return SHELL(
    <Card>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1C1E]">Welcome back</h1>
      <p className="mt-2 text-base text-gray-600">Log in to your account</p>

      <div className="mt-6 space-y-4">
        <GoogleButton onClick={loginWithGoogle} />
        <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-gray-500">
          <div className="h-px flex-1 bg-gray-200"></div>
          Or
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-4 space-y-4" data-testid="login-form">
        {err && <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]" data-testid="login-error">{err}</div>}

        <Field label="Email" name="email" type="email" value={email} onChange={setEmail} required placeholder="you@example.com" />
        <Field label="Password" name="password" type="password" value={password} onChange={setPassword} required placeholder="Enter your password" />

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm font-medium text-[#007AFF] hover:underline">Forgot password?</Link>
        </div>

        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-base font-semibold text-white transition hover:bg-[#333] disabled:opacity-50">
          {busy ? 'Logging in…' : 'Log in'}
        </button>

        <p className="pt-2 text-center text-base text-gray-600">
          Don't have an account? <Link to="/register" className="font-semibold text-[#007AFF] hover:underline">Create one</Link>
        </p>
      </form>
    </Card>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      const res = await AUTH_API.register(email, password, fullName);
      setPendingEmail(res.user_id ? email : email);
      setDevOtp(res.dev_otp || null);
      if (res.requires_verification) {
        // Move to OTP step
        nav(`/verify-otp?email=${encodeURIComponent(email)}`);
      } else {
        const v = await AUTH_API.verifyOtp(email, '000000');
        setAuth(v);
        nav('/portal/dashboard', { replace: true });
      }
    } catch (e: any) {
      setErr(e?.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  return SHELL(
    <Card>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1C1E]">Create account</h1>
      <p className="mt-2 text-base text-gray-600">Get started with Funder Pro</p>

      <div className="mt-6">
        <GoogleButton />
      </div>
      <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wider text-gray-500">
        <div className="h-px flex-1 bg-gray-200"></div>
        Or
        <div className="h-px flex-1 bg-gray-200"></div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {err && <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]">{err}</div>}

        <Field label="Full name" name="full_name" value={fullName} onChange={setFullName} placeholder="Your name" />
        <Field label="Email" name="email" type="email" value={email} onChange={setEmail} required placeholder="you@example.com" />
        <Field label="Password" name="password" type="password" value={password} onChange={setPassword} required placeholder="At least 8 characters" />
        <Field label="Confirm password" name="confirm" type="password" value={confirm} onChange={setConfirm} required placeholder="Re-enter your password" />

        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-base font-semibold text-white transition hover:bg-[#333] disabled:opacity-50">
          {busy ? 'Creating…' : 'Create account'}
        </button>

        <p className="pt-2 text-center text-base text-gray-600">
          Already have an account? <Link to="/login" className="font-semibold text-[#007AFF] hover:underline">Log in</Link>
        </p>
      </form>
    </Card>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await AUTH_API.forgot(email);
      setSent(true);
    } catch (e: any) {
      setErr(e?.message || 'Failed to send reset email');
    } finally {
      setBusy(false);
    }
  }

  return SHELL(
    <Card>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1C1E]">Forgot password</h1>
      <p className="mt-2 text-base text-gray-600">Enter your email and we'll send a reset link</p>

      {sent ? (
        <div className="mt-6 rounded-xl border border-[#34C759]/30 bg-[#34C759]/10 p-4 text-sm text-[#0a5e1c]">
          If an account exists for {email}, a reset link is on its way.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {err && <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]">{err}</div>}
          <Field label="Email" name="email" type="email" value={email} onChange={setEmail} required placeholder="you@example.com" />
          <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-base font-semibold text-white transition hover:bg-[#333] disabled:opacity-50">
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}

      <p className="mt-4 text-center text-base text-gray-600">
        <Link to="/login" className="font-semibold text-[#007AFF] hover:underline">Back to log in</Link>
      </p>
    </Card>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await AUTH_API.reset(token, password);
      setDone(true);
    } catch (e: any) {
      setErr(e?.message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  }

  return SHELL(
    <Card>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1C1E]">Reset password</h1>
      <p className="mt-2 text-base text-gray-600">Choose a new password for your account</p>

      {done ? (
        <div className="mt-6 space-y-4">
          <div className="rounded-xl border border-[#34C759]/30 bg-[#34C759]/10 p-4 text-sm text-[#0a5e1c]">Password reset. You can now log in.</div>
          <Link to="/login" className="block w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-center text-base font-semibold text-white">Go to log in</Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {err && <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]">{err}</div>}
          <Field label="New password" name="password" type="password" value={password} onChange={setPassword} required />
          <Field label="Confirm new password" name="confirm" type="password" value={confirm} onChange={setConfirm} required />
          <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-base font-semibold text-white transition hover:bg-[#333] disabled:opacity-50">
            {busy ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      )}
    </Card>
  );
}

export function VerifyOtpPage() {
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const nav = useNavigate();
  const [otp, setOtp] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const v = await AUTH_API.verifyOtp(email, otp);
      setAuth(v);
      nav('/portal/dashboard', { replace: true });
    } catch (e: any) {
      setErr(e?.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  return SHELL(
    <Card>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1C1E]">Verify your email</h1>
      <p className="mt-2 text-base text-gray-600">We sent a 6-digit code to {email}</p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        {err && <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 text-sm text-[#FF3B30]">{err}</div>}
        <Field label="Verification code" name="otp" value={otp} onChange={setOtp} required placeholder="Enter 6-digit code" />
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1C1C1E] px-6 py-3.5 text-base font-semibold text-white transition hover:bg-[#333] disabled:opacity-50">
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <p className="text-center text-sm text-gray-600">
          Didn't get a code? <button type="button" onClick={() => AUTH_API.resendOtp(email).catch(() => {})} className="font-semibold text-[#007AFF] hover:underline">Resend</button>
        </p>
      </form>
    </Card>
  );
}
